import { gql } from './client';
import { emit } from './events';

// House-level OS map tiles for the drawing map. The OS key stays on the locate service, so tiles come through the Gateway as
// base64 entities (ElevationsPlanningOsMapTile). Requests made together (a screenful, as the map asks for them) are sent as one
// Gateway call. Zoom 16 is free OS open data; 17 to 20 are premium and charged to the OS project.

export const OS_MAP_MIN_ZOOM = 16;
// OS's deepest zoom; house numbers only appear from 19. Each case is limited to its site's area (planning-locate-service tileQuota.ts).
export const OS_MAP_MAX_ZOOM = 20;
export const OS_MAP_ATTRIBUTION = '© Crown copyright and database rights Ordnance Survey';

const BATCH_WAIT_MS = 25;
const MAX_BATCH = 24;
// Shown where OS has no tile, so the map draws nothing rather than an error.
const TRANSPARENT_PNG = Uint8Array.from(atob('iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='), c => c.charCodeAt(0));

export interface Pin { lat: number; lon: number }
interface Pending { id: string; caseId: string | null; pin: Pin | null; reachM: number; preview: boolean; resolve: (b: ArrayBuffer) => void; reject: (e: Error) => void }

const batchKey = (p: { caseId: string | null; pin: Pin | null; reachM: number; preview: boolean }) =>
  `${p.caseId ?? '-'}|${p.preview ? '~' : ''}${p.pin ? `${p.pin.lat},${p.pin.lon}` : '-'}|${p.reachM}`;

let queue: Pending[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<(message: string | null) => void>();
const allowanceListeners = new Set<() => void>();
// An allowance message stays with its case for the rest of the visit.
const allowanceMessages = new Map<string, string>();

/** Hears when the detailed map stops (or starts) working, with a sentence saying why, so the page can show it. */
export function onOsMapProblem(fn: (message: string | null) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Hears when an allowance of premium tiles is used up, so the map can fall back to the open basemap. */
export function onOsMapAllowanceUsed(fn: () => void): () => void {
  allowanceListeners.add(fn);
  return () => { allowanceListeners.delete(fn); };
}

function report(message: string | null) {
  listeners.forEach(fn => fn(message));
}

// A case gets the detailed map only around its applicant's pin.
const PREVIEW_OUTSIDE_MESSAGE = 'Until you confirm your pin, the more detailed map covers about 1 km around the place you searched for. Search again to look somewhere else.';
const OUTSIDE_SITE_MESSAGE ='The most detailed map covers the area around your pin, and grows to cover your outline as you draw it (up to 1 km). To look somewhere else, start a new check.';

// The premium allowances refill when an assessment of the case completes (see planning-locate-service tileQuota.ts).
const WITHHELD_MESSAGES: Record<string, string> = {
  UserAllowance: "You've used your allowance of the most detailed map. It refills each time you complete an assessment; until then the map shows a little less detail when zoomed right in.",
  CaseAllowance: "This site has used its allowance of the most detailed map. It refills when the case is assessed; until then the map shows a little less detail when zoomed right in.",
};

// Tiles already received, kept in memory for the visit so zooming or panning back, and the area growing with the outline,
// redraw at once instead of fetching (and paying for) the same tile again. A tile's image is the same whichever case or pin
// asked for it, so it is kept by its own id. Only tiles actually served are kept: a withheld one (outside the area, or an
// allowance used up) is asked for again. OS allows caching for up to 24 hours; nothing here is stored beyond the page.
const MAX_CACHED_TILES = 1000;
const CACHE_TTL_MS = 23 * 60 * 60 * 1000;
const tileCache = new Map<string, { bytes: ArrayBuffer; at: number; premium: boolean }>();

// Premium tiles start at this zoom (planning-locate-service osMapTiles.ts).
const PREMIUM_ZOOM = 17;

function cachedTile(id: string): { bytes: ArrayBuffer; premium: boolean } | null {
  const hit = tileCache.get(id);
  if (!hit) return null;
  tileCache.delete(id);
  if (Date.now() - hit.at > CACHE_TTL_MS) return null;
  tileCache.set(id, hit);
  // A copy: the map may hand the buffer to a worker, which detaches it.
  return { bytes: hit.bytes.slice(0), premium: hit.premium };
}

function keepTile(id: string, bytes: ArrayBuffer, premium: boolean) {
  tileCache.delete(id);
  tileCache.set(id, { bytes: bytes.slice(0), at: Date.now(), premium });
  while (tileCache.size > MAX_CACHED_TILES) tileCache.delete(tileCache.keys().next().value!);
}

// Premium tiles drawn again from this memory never reach the server, so the page reports them for each case (a while after
// the map settles, or as the page is left), and the case shows them as cached, at what they would have cost.
const REUSE_REPORT_WAIT_MS = 5000;
const reusedByCase = new Map<string, number>();
let reuseTimer: ReturnType<typeof setTimeout> | undefined;

function reportReuse() {
  clearTimeout(reuseTimer);
  reuseTimer = undefined;
  for (const [caseId, tiles] of reusedByCase) {
    // Only a display figure: a report that fails is not worth retrying.
    emit('PlanningMapTilesReused', caseId, { tiles }).catch(() => undefined);
  }
  reusedByCase.clear();
}

function noteReuse(caseId: string) {
  reusedByCase.set(caseId, (reusedByCase.get(caseId) ?? 0) + 1);
  clearTimeout(reuseTimer);
  reuseTimer = setTimeout(reportReuse, REUSE_REPORT_WAIT_MS);
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden' && reusedByCase.size) reportReuse(); });
}

function decode(data: string | null): ArrayBuffer {
  const bytes = data ? Uint8Array.from(atob(data), c => c.charCodeAt(0)) : TRANSPARENT_PNG;
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

async function flush() {
  timer = undefined;
  // One request per case and pin: the server records the premium tiles it pays for against the case they were drawn for,
  // and serves them only around that case's pin.
  const key = batchKey(queue[0]);
  const caseId = queue[0]?.caseId ?? null;
  const pin = queue[0]?.pin ?? null;
  const reachM = queue[0]?.reachM ?? 0;
  const preview = queue[0]?.preview ?? false;
  const same = queue.filter(p => batchKey(p) === key).slice(0, MAX_BATCH);
  queue = queue.filter(p => !same.includes(p));
  const batch = same;
  if (queue.length) timer = setTimeout(() => void flush(), 0);
  try {
    const data = await gql<{ elevationsPlanningOsMapTiles: { id: string; data: string | null; withheld: string | null }[] | null }>(
      'query OsMapTiles($ids: [ID!]!, $caseId: ID, $pinLat: Float, $pinLon: Float, $reachM: Int, $previewLat: Float, $previewLon: Float) { elevationsPlanningOsMapTiles(ids: $ids, caseId: $caseId, pinLat: $pinLat, pinLon: $pinLon, reachM: $reachM, previewLat: $previewLat, previewLon: $previewLon) { id data withheld } }',
      {
        ids: [...new Set(batch.map(p => p.id))], caseId, reachM: reachM || null,
        // Finding the site, the point is the place searched for (a preview around it); after that, the confirmed pin.
        pinLat: preview ? null : pin?.lat ?? null, pinLon: preview ? null : pin?.lon ?? null,
        previewLat: preview ? pin?.lat ?? null : null, previewLon: preview ? pin?.lon ?? null : null,
      },
    );
    const tiles = data.elevationsPlanningOsMapTiles ?? [];
    const byId = new Map(tiles.map(t => [t.id, t]));
    for (const p of batch) {
      const t = byId.get(p.id);
      const bytes = decode(t?.data ?? null);
      if (t && !t.withheld) keepTile(p.id, bytes, t.data != null && Number(p.id.split('-')[0]) >= PREMIUM_ZOOM);
      p.resolve(bytes);
    }
    const withheld = tiles.find(t => t.withheld && WITHHELD_MESSAGES[t.withheld])?.withheld;
    if (withheld) {
      allowanceMessages.set(caseId ?? '-', WITHHELD_MESSAGES[withheld]);
      allowanceListeners.forEach(fn => fn());
    }
    // Only the detailed map's own requests say how it is doing: the free map's must not clear an allowance message.
    if (caseId) {
      const outside = tiles.some(t => t.withheld === 'OutsideSite');
      report(allowanceMessages.get(caseId) ?? (outside ? (preview ? PREVIEW_OUTSIDE_MESSAGE : OUTSIDE_SITE_MESSAGE) : null));
    }
  } catch (e) {
    const message = e instanceof Error ? e.message.replace(/^Downstream service error: /, '') : String(e);
    report(`The detailed map can't be shown just now (${message}). You can still draw on the standard map.`);
    for (const p of batch) p.reject(new Error(message));
  }
}

/**
 * One tile, "z-x-y", as PNG bytes, for the case being drawn around its pin, with the outline reaching reachM from it so far;
 * tiles asked for together share one request.
 */
export function loadOsMapTile(id: string, caseId: string | null, pin: Pin | null, reachM = 0, preview = false): Promise<ArrayBuffer> {
  const cached = cachedTile(id);
  if (cached) {
    if (cached.premium && caseId) noteReuse(caseId);
    return Promise.resolve(cached.bytes);
  }
  return new Promise((resolve, reject) => {
    queue.push({ id, caseId, pin, reachM, preview, resolve, reject });
    if (queue.length >= MAX_BATCH) { clearTimeout(timer); void flush(); return; }
    timer ??= setTimeout(() => void flush(), BATCH_WAIT_MS);
  });
}
